import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import { AccountData, Event, EventType } from "@markit/common.types";
import {
  isExternalGenericLink,
  isEventRelatedType,
  LessThanDate,
  GetDateWithTime,
  checkTimezoneDiffFromSystem,
} from "@markit/common.utils";
import { useNavigate } from "../../hooks/useNavigate";
import { Colors } from "../../utils/colors";
import { DataLoaders } from "../../redux/slices/dataSlice";
import EventItemPreviewType from "../DisplayItem/EventItem/EventItemPreviewType";
import EventItemImage from "../DisplayItem/EventItem/EventItemImage";
import { NewProductNavigationId } from "../../navigation/AppParamList";
import LocalSystemDateDisplay from "../FullEvent/LocalSystemDateDisplay";
import { ThemeStyle, useTheme } from "../../hooks/useTheme";
import ButtonWithDropdown from "../Buttons/ButtonWithDropdown";
import { DropdownMenuItem } from "../DropdownMenu";
import CloneProductModal from "../EventDashboard/DashboardModals/CloneProductModal";

type FeaturedEventCardProps = {
  event: Event;
  eventOrganizers?: AccountData[];
  addToSearchHistory?: (eventId: string) => void;
  myEvent?: boolean;
  altSize?: number;
};

export const EventContentMobile = (props: {
  event: Event;
  altSize?: number;
  altFontSize?: number;
  eventTypeColor?: string;
  theme?: ThemeStyle;
  myEvent?: boolean;
  showSystemTime?: boolean;
}) => {
  const {
    event: e,
    altSize,
    altFontSize,
    eventTypeColor,
    theme,
    myEvent,
    showSystemTime,
  } = props;

  // We display the start date if it's a current event, otherwise we display the createdAt date
  const displayDate = useCallback(
    (showStart: boolean) => {
      const isLiveEventType = isEventRelatedType(e.eventType) && !e.isDraft;
      const isCurrentEvent =
        isLiveEventType && LessThanDate(new Date().toISOString(), e.end);

      if (isCurrentEvent) {
        return showStart ? e.start : e.end;
      }

      if (isLiveEventType) {
        return e.end;
      }

      return e.createdAt;
    },
    [e.eventType, e.isDraft, e.end, e.createdAt, e.start]
  );

  return (
    <div className="AlignedRowSelect">
      <div>
        <EventItemImage
          item={e}
          size={altSize ?? 84}
          scaleImage
          containerStyles={{ marginRight: "14px", cursor: "pointer" }}
        />
      </div>
      <div className="ColumnNormalSelect" style={{ gap: "4px" }}>
        <span
          className="OneLineText"
          style={{
            fontSize: altFontSize ?? 14,
            fontWeight: 500,
            ...theme?.PrimaryText,
          }}
        >
          {e.title !== "" ? e.title : "Untitled"}
        </span>
        <span
          className="OneLineText"
          style={{
            fontSize: altFontSize ?? 12,
            color: Colors.GRAY1,
            width: isExternalGenericLink(e.eventType) ? 200 : "auto",
          }}
        >
          {displayDate(true) ? (
            <span>
              {isEventRelatedType(e.eventType) && !e.isDraft
                ? LessThanDate(new Date().toISOString(), displayDate(false))
                  ? "Starts:"
                  : "Ended:"
                : "Created:"}{" "}
              {showSystemTime && checkTimezoneDiffFromSystem(e.timezone) ? (
                <span>
                  {GetDateWithTime(displayDate(true), true)}
                  <LocalSystemDateDisplay
                    timestamp={displayDate(true)}
                    timezone={e.timezone}
                  />
                </span>
              ) : (
                GetDateWithTime(displayDate(true), true, e.timezone)
              )}
            </span>
          ) : (
            "Date Unselected"
          )}
        </span>
        {myEvent ? (
          <EventItemPreviewType
            eventItem={e}
            altFontSize={altFontSize}
            altBackgroundColor={eventTypeColor}
          />
        ) : (
          <span
            className="OneLineText"
            style={{ fontSize: altFontSize ?? 12, color: Colors.GRAY1 }}
          >
            {e.isVirtual
              ? "Virtual Event"
              : e.formattedAddress
                ? e.formattedAddress
                : "Location Unselected"}
          </span>
        )}
      </div>
    </div>
  );
};

function timeout(delay: number) {
  // eslint-disable-next-line promise/param-names
  return new Promise((res) => setTimeout(res, delay));
}

export const FeaturedEventCardMobile = (props: FeaturedEventCardProps) => {
  const { event: e, addToSearchHistory, myEvent, altSize } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);

  const navigateItem = useCallback(async () => {
    dispatch(DataLoaders.event(e.id));
    // TODO: should be able to deprecate this once we refactor the product panel items
    if (myEvent) {
      if (e.isDraft) {
        navigate(
          e.eventType === EventType.WEBINAR
            ? NewProductNavigationId.WEBINAR
            : NewProductNavigationId.EVENT,
          {
            state: { eventId: e.id },
          }
        );
      } else {
        navigate(`/e/${e.id}/dashboard`, { state: { eventId: e.id } });
      }
    } else {
      if (addToSearchHistory) {
        addToSearchHistory(e.id);
        // Artificial delay to save clicked event to search history before nav
        await timeout(1000);
      }
      navigate(`/e/${e.id}`, { state: { eventId: e.id } });
    }
  }, [
    addToSearchHistory,
    dispatch,
    e.id,
    e.isDraft,
    e.eventType,
    myEvent,
    navigate,
  ]);

  const dropdownItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "View",
        icon: "ion:arrow-forward",
        key: "view",
        onPress: navigateItem,
      },
      {
        title: "Clone",
        icon: "ion:copy",
        key: "clone",
        onPress: () => {
          setShowCloneModal(true);
          setShowDropdown(false);
        },
      },
    ],
    [navigateItem]
  );

  return (
    <>
      <div className="AlignedRowSpaced" onClick={navigateItem}>
        <EventContentMobile
          event={e}
          myEvent={myEvent}
          showSystemTime
          altSize={altSize}
        />
        {myEvent ? (
          <div
            style={{ marginRight: "14px" }}
            onClick={(clickEvent) => clickEvent.stopPropagation()}
          >
            <ButtonWithDropdown
              buttonLabel="View"
              textColor={Colors.BLACK}
              isOpenDropdown={showDropdown}
              setIsOpenDropdown={setShowDropdown}
              onPress={navigateItem}
              dropdownItems={dropdownItems}
              containerStyles={{ border: `1px solid ${theme.SubText.color}` }}
            />
          </div>
        ) : (
          <Icon
            icon={"mdi:chevron-right"}
            height={24}
            style={{
              color: Colors.GRAY1,
              marginRight: "14px",
              cursor: "pointer",
            }}
          />
        )}
      </div>
      {showCloneModal ? (
        <CloneProductModal
          event={e}
          isModalVisible={showCloneModal}
          setIsModalVisible={setShowCloneModal}
        />
      ) : null}
    </>
  );
};

type FeaturedEventCardMobileSearchProps = {
  event: Event;
  deleteOnClick: () => void;
};

export const FeaturedEventCardMobileSearch = (
  props: FeaturedEventCardMobileSearchProps
) => {
  const { event: e, deleteOnClick } = props;

  return (
    <div className="AlignedRowSpaced">
      <Link
        reloadDocument
        style={{ textDecoration: "none", color: "black", cursor: "pointer" }}
        to={`/e/${e.id}`}
        key={e.id}
      >
        <EventContentMobile event={e} showSystemTime />
      </Link>
      <Icon
        icon={"ion:close-outline"}
        height={24}
        style={{ color: Colors.GRAY1, marginRight: "14px", cursor: "pointer" }}
        onClick={deleteOnClick}
      />
    </div>
  );
};

type RenderedEventsMassTextsModalProps = {
  event: Event;
  disabled?: boolean;
  hideType?: boolean;
};

export const RenderedEventMassTextsModal = (
  props: RenderedEventsMassTextsModalProps
) => {
  const { event: e, disabled, hideType } = props;

  const styles = {
    truncatedText: {
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      width: 360,
    },
    description: {
      fontSize: 12,
      color: Colors.GRAY1,
    },
  };

  return (
    <div
      className="AlignedRowSpacedSelect"
      style={{ pointerEvents: disabled ? "none" : "all" }}
    >
      <div className="AlignedRow" style={{ gap: 14 }}>
        <EventItemImage
          item={e}
          size={48}
          containerStyles={{ backgroundColor: Colors.WHITE }}
        />
        <div className="ColumnNormalSelect" style={{ gap: "4px" }}>
          <p style={{ ...styles.truncatedText, fontSize: 12, fontWeight: 500 }}>
            {e.title ?? "Untitled"}
          </p>
          <span className="smallBodySubtext OneLineText" style={{ width: 340 }}>
            {isExternalGenericLink(e.eventType)
              ? e.externalLink
              : isEventRelatedType(e.eventType)
                ? e.start
                  ? `${GetDateWithTime(e.start, true, e.timezone)}`
                  : "Date Unselected"
                : `Created: ${GetDateWithTime(e.createdAt, true)}`}
          </span>
          {!hideType ? <EventItemPreviewType eventItem={e} /> : null}
        </div>
      </div>
      {!disabled ? (
        <Icon
          icon={"ion:chevron-right"}
          height={16}
          style={{ color: Colors.GRAY1, cursor: "pointer" }}
        />
      ) : null}
    </div>
  );
};
